/**
 * @fileoverview Authentication hook.
 * @summary Manages authentication state and user information.
 * @package PlayItNow
 * @module apps/front/hooks/useAuth
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { get, post } from '@/services/api'
import type { AuthResponse, User } from '@playitnow/shared'

/**
 * @summary Authentication state.
 */
interface AuthState {
  /** Current authenticated user (null when logged out) */
  user: User | null
  /** Whether the auth check is in progress */
  isLoading: boolean
  /** Whether the user is authenticated */
  isAuthenticated: boolean
}

/**
 * @summary Authentication hook return type.
 */
interface UseAuthReturn extends AuthState {
  /** Re-check authentication status */
  checkAuth: () => Promise<void>
  /** Log out the current user */
  logout: () => Promise<void>
}

/**
 * @summary Hook for managing authentication state.
 * @returns Authentication state and methods.
 */
export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  })
  const navigate = useNavigate()

  /**
   * @summary Check authentication status.
   */
  const checkAuth = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }))
      const response = await get<AuthResponse & { user: AuthResponse['user'] | null }>('/auth/me')
      setState({
        user: response.user ?? null,
        isLoading: false,
        isAuthenticated: response.user != null,
      })
    } catch {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      })
    }
  }, [])

  /**
   * @summary Logout user.
   */
  const logout = useCallback(async () => {
    try {
      await post('/auth/logout', {})
    } catch {
      // Ignore errors on logout
    } finally {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      })
      navigate('/login')
    }
  }, [navigate])

  // Check auth on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return {
    ...state,
    checkAuth,
    logout,
  }
}
